import React, { Component } from 'react';
import styled from 'styled-components';
import { system } from './system';
import { visuallyHidden } from './visuallyHidden';

// Replicated from molekule/lib/Form/Checkbox.js
//
// The app uses TWO different call conventions:
//   1. molekule style: value/valueTrue/valueFalse + onChange(name, newValue)
//      e.g. CheckboxGroup.js, GeneralInformationFields.js
//   2. HTML style: checked + onChange(event)
//      e.g. GoogleContactsModal.js passes checked={bool} and onChange={handler}
//
// We normalise both: if `checked` is passed explicitly we use it as the
// controlled boolean; otherwise we derive checked from value === valueTrue.

const transitionTiming = '250ms cubic-bezier(0.4, 0, 0.2, 1)';

interface CheckboxShapeProps {
  $isChecked?: boolean;
  $isFocused?: boolean;
  $isRadio?: boolean;
  $color?: string;
  $size?: string;
}

const HiddenInput = styled.input`
  ${visuallyHidden}
  left: 1px;
`;

const CheckboxShape = styled.div<CheckboxShapeProps>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-style: solid;
  border-radius: ${(p: CheckboxShapeProps) => (p.$isRadio ? '50%' : '2px')};
  transition:
    background-color ${transitionTiming},
    border-color ${transitionTiming};

  /* size md defaults */
  height: ${(p: CheckboxShapeProps) => (p.$isRadio ? '20px' : '18px')};
  width: ${(p: CheckboxShapeProps) => (p.$isRadio ? '20px' : '18px')};
  min-width: ${(p: CheckboxShapeProps) => (p.$isRadio ? '20px' : '18px')};
  border-width: 2px;
  margin-top: 1px;

  border-color: ${(p: CheckboxShapeProps & { theme?: any }) => {
    if (p.$isChecked && p.theme && p.theme.colors && p.$color) {
      return p.theme.colors[p.$color] || p.$color;
    }
    return p.theme && p.theme.colors ? p.theme.colors.greyDark : '#A4A6AA';
  }};

  background-color: ${(p: CheckboxShapeProps & { theme?: any }) => {
    if (p.$isChecked && p.theme && p.theme.colors && p.$color) {
      return p.theme.colors[p.$color] || p.$color;
    }
    return 'transparent';
  }};

  &:before,
  &:after {
    content: '';
    position: absolute;
    opacity: 0;
    transition: opacity ${transitionTiming};
    box-sizing: border-box;
  }

  &:before {
    border: 4px solid
      ${(p: CheckboxShapeProps & { theme?: any }) =>
        p.theme && p.theme.colors
          ? p.theme.colors.colorFocus || '#CADCFF'
          : '#CADCFF'};
    border-radius: 6px;
    box-sizing: content-box;
    z-index: -1;
    /* molekule sizes.Checkbox.md[':before'] = { width: 18px, height: 18px } */
    width: ${(p: CheckboxShapeProps) => (p.$isRadio ? '20px' : '18px')};
    height: ${(p: CheckboxShapeProps) => (p.$isRadio ? '20px' : '18px')};
  }

  ${(p: CheckboxShapeProps) => (p.$isFocused ? `&:before { opacity: 1; }` : '')}

  ${(p: CheckboxShapeProps) =>
    p.$isRadio && p.$isChecked
      ? `
        &:after {
          opacity: 1;
          border-radius: 50%;
          border-style: solid;
          border-color: white;
          width: 16px;
          height: 16px;
          border-width: 3px;
        }
      `
      : ''}
`;

// molekule sizes.CheckboxLabel.md = { fontSize: '16px', marginLeft: '10px' }
const CheckboxLabelSpan = styled.span`
  transition: color ${transitionTiming};
  font-size: 16px;
  margin-left: 10px;
`;

// molekule sizes.CheckIcon.md = { fontSize: '20px' }
// The check icon in molekule uses an MDI font icon (class-based), rendered via Icon component.
// We replicate with a unicode ✓ pseudo-element at the correct 20px size.
const CheckIcon = styled.span<{ $isChecked?: boolean }>`
  position: absolute;
  color: white;
  opacity: ${(p: { $isChecked?: boolean }) => (p.$isChecked ? 1 : 0)};
  transition: opacity ${transitionTiming};
  font-size: 20px;
  line-height: 1;

  &::after {
    content: '✓';
    display: block;
  }
`;

interface ContainerProps {
  $isChecked?: boolean;
  $isDisabled?: boolean;
  $isHorizontal?: boolean;
  $sys?: Record<string, any>;
}

// molekule sizes.CheckboxContainer.md = { minHeight: '30px' }
// Disabled: cursor not-allowed; CheckboxShape border-color grey; CheckboxLabelSpan color grey;
// if checked, CheckboxShape background-color also grey (all via direct child selectors).
const CheckboxContainer = styled.label<ContainerProps>`
  display: inline-flex;
  align-items: flex-start;
  justify-content: flex-start;
  margin-bottom: 4px;
  user-select: none;
  cursor: ${(p: ContainerProps) => (p.$isDisabled ? 'not-allowed' : 'pointer')};
  position: relative;
  min-height: 30px;

  ${(p: ContainerProps & { theme?: any }) =>
    p.$isDisabled && p.theme && p.theme.colors
      ? `
        cursor: not-allowed;
        ${CheckboxShape} {
          border-color: ${p.theme.colors.grey || '#D1D2D5'} !important;
          background-color: transparent !important;
        }
        ${CheckboxLabelSpan} {
          color: ${p.theme.colors.grey || '#D1D2D5'};
        }
        ${p.$isChecked ? `${CheckboxShape} { background-color: ${p.theme.colors.grey || '#D1D2D5'} !important; }` : ''}
      `
      : ''}

  ${(p: any) => system(p.$sys, p.theme)}
`;

const FormError = styled.span`
  display: block;
  color: ${(p: any) =>
    p.theme && p.theme.colors
      ? p.theme.colors.red || p.theme.colors.danger
      : '#FD575D'};
  font-size: 12px;
  margin-top: 4px;
`;

export interface CheckboxProps {
  id: string;
  name: string;
  label?: React.ReactNode;
  // molekule-style controlled: value compared to valueTrue
  value?: any;
  valueTrue?: any;
  valueFalse?: any;
  // HTML-style controlled: boolean checked prop
  checked?: boolean;
  onChange?: (nameOrEvent: any, newValue?: any) => void;
  disabled?: boolean;
  error?: string;
  size?: string;
  isRadio?: boolean;
  horizontal?: boolean;
  colorOn?: string;
  colorOff?: string;
  ariaLabel?: string;
  styles?: Record<string, any>;
  className?: string;
  style?: React.CSSProperties;
  [key: string]: any;
}

interface CheckboxState {
  value: any;
  isFocused: boolean;
}

class Checkbox extends Component<CheckboxProps, CheckboxState> {
  static defaultProps = {
    valueTrue: true,
    valueFalse: false,
    colorOn: 'primary',
    colorOff: 'greyDark',
    horizontal: false,
    disabled: false,
    styles: {},
    label: null,
    isRadio: false,
  };

  constructor(props: CheckboxProps) {
    super(props);
    this.state = {
      value: props.value,
      isFocused: false,
    };
  }

  static getDerivedStateFromProps(props: CheckboxProps, state: CheckboxState) {
    if (props.value !== undefined && props.value !== state.value) {
      return { value: props.value };
    }
    return null;
  }

  get isChecked(): boolean {
    const { checked, valueTrue } = this.props;
    // If caller passes `checked` (HTML API), use it directly
    if (checked !== undefined) {
      return !!checked;
    }
    // molekule API: compare stored value to valueTrue
    return this.state.value === valueTrue;
  }

  handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { onChange, name, valueTrue, valueFalse, checked } = this.props;
    if (!onChange) return;

    // HTML-style API: pass the event directly
    if (checked !== undefined) {
      onChange(e);
      return;
    }

    // molekule API: toggle and call onChange(name, newValue)
    const newValue = this.isChecked ? valueFalse : valueTrue;
    this.setState({ value: newValue }, () => {
      onChange(name, newValue);
    });
  };

  handleFocus = () => {
    this.setState({ isFocused: true });
  };

  handleBlur = () => {
    this.setState({ isFocused: false });
  };

  render() {
    const {
      id,
      name,
      label,
      disabled,
      error,
      isRadio,
      colorOn,
      colorOff,
      ariaLabel,
      className,
      style,
    } = this.props;

    const { isFocused } = this.state;
    const isChecked = this.isChecked;
    const color = isChecked ? colorOn : colorOff;

    return (
      <React.Fragment>
        <CheckboxContainer
          htmlFor={id}
          $isChecked={isChecked}
          $isDisabled={disabled}
          className={className}
          style={style}
        >
          <HiddenInput
            aria-label={
              ariaLabel || (typeof label === 'string' ? label : undefined)
            }
            id={id}
            name={name}
            type="checkbox"
            checked={isChecked}
            disabled={disabled}
            onChange={this.handleChange}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
          />
          <CheckboxShape
            $isChecked={isChecked}
            $isFocused={isFocused}
            $isRadio={isRadio}
            $color={color}
          >
            {isChecked && !isRadio && <CheckIcon $isChecked={isChecked} />}
          </CheckboxShape>
          {label && <CheckboxLabelSpan>{label}</CheckboxLabelSpan>}
        </CheckboxContainer>
        {!isFocused && error ? <FormError>{error}</FormError> : null}
      </React.Fragment>
    );
  }
}

export default Checkbox;
